import type {
  AmbiguityNoteId,
  AssetId,
  BeatId,
  CaptionTokenId,
  CastSlotId,
  CastVariantId,
  ClaimId,
  EntityId,
  Milli,
  ScenarioId,
  ScoringTagId,
  Tick,
  TrendId,
} from "./ids.js";
import type { RenderVariant } from "./media-binding.js";

/** Bump when breaking SourcePack shape. Consumers must reject unknown versions. */
export const SOURCE_PACK_SCHEMA_VERSION = 1 as const;
export type SourcePackSchemaVersion = typeof SOURCE_PACK_SCHEMA_VERSION;

export type ContentRating = "everyone" | "teen" | "mature";

export interface LicenseManifest {
  /** Human-readable summary of pack licensing. */
  summary: string;
  /** Attribution lines required on export. */
  attributions: string[];
  /** SPDX-like or free-text license labels for the pack as a whole. */
  packLicense: string;
}

export interface CanonicalEventRecord {
  /** Author-facing title for the complete authored event. */
  title: string;
  /** Full prose description of what happened in the authored scenario. */
  summary: string;
  /** Ordered beat IDs that constitute the complete record. */
  beatOrder: BeatId[];
}

export interface SemanticBeat {
  id: BeatId;
  label: string;
  /** Inclusive start tick in source timeline. */
  startTick: Tick;
  /** Exclusive end tick in source timeline. */
  endTick: Tick;
  /** Entities visible or audible in this beat. */
  entityIds: EntityId[];
  /** Dialogue or spoken lines authored for this beat. */
  dialogue: string[];
  /** Whether omitting this beat removes important context. */
  contextCritical: boolean;
  /** Emotional intensity contribution when retained (millipoints 0–1000). */
  intensityMilli: Milli;
  /** Hook strength when this beat opens the reel. */
  hookMilli: Milli;
  scoringTagIds: ScoringTagId[];
}

export type EntityKind = "person" | "animal" | "object" | "place" | "other";

export interface Entity {
  id: EntityId;
  name: string;
  kind: EntityKind;
  /** True when excluding this entity from frame removes context. */
  contextCarrier: boolean;
  description: string;
}

export interface Claim {
  id: ClaimId;
  /** Player-facing claim text (for scoring/debug, not a moral label). */
  statement: string;
  /** Beat IDs whose presence supports this claim. */
  supportingBeatIds: BeatId[];
  /** Beat IDs that contradict this claim when retained. */
  contradictingBeatIds: BeatId[];
  /** Caption token combinations that imply this claim. */
  implyingTokenGroups: CaptionTokenId[][];
  /** How severe an unsupported implication of this claim is (0–1000). */
  driftWeightMilli: Milli;
}

export interface AmbiguityNote {
  id: AmbiguityNoteId;
  text: string;
  relatedBeatIds: BeatId[];
  relatedClaimIds: ClaimId[];
}

export type CaptionTokenRole =
  | "subject"
  | "object"
  | "action"
  | "motive"
  | "qualifier"
  | "hook"
  | "other";

export interface CaptionToken {
  id: CaptionTokenId;
  text: string;
  role: CaptionTokenRole;
  /**
   * Claims implied by this token alone.
   * Reserve for unambiguous single-token claims (e.g. ABANDONED).
   * Generic words (THEY, LEFT) must use Claim.implyingTokenGroups instead.
   */
  claimIds: ClaimId[];
  intensityMilli: Milli;
}

export interface CaptionVocabulary {
  tokens: CaptionToken[];
}

export interface CaptionGrammarRule {
  id: string;
  /** Ordered roles that form a valid caption pattern. */
  roles: CaptionTokenRole[];
}

export interface CaptionGrammar {
  rules: CaptionGrammarRule[];
  maxTokens: number;
}

export type AssetKind =
  | "visual"
  | "audio"
  | "sticker"
  | "font"
  | "effect"
  | "other";

/**
 * How a manifest entry resolves to bytes or authored content.
 * - `file`: `path` is a public asset path under the game public root.
 * - `procedural`: no packed binary; `sourceUrl` is a stable `repo:` URI (+ optional #fragment).
 */
export type AssetSourceKind = "file" | "procedural";

export interface AssetManifestEntry {
  id: AssetId;
  kind: AssetKind;
  /**
   * Public relative path for `sourceKind: "file"` (e.g. `assets/models/…`).
   * Empty string for `sourceKind: "procedural"` (no packed placeholder binary).
   * For audio, prefer Ogg Opus here; use `fallbackPath` for AAC/M4A when needed.
   */
  path: string;
  /**
   * Optional alternate public-relative path (audio AAC/M4A for Safari/WebKit).
   * Runtime prefers `path` when the engine can decode it.
   */
  fallbackPath?: string;
  license: string;
  author: string;
  sourceUrl: string;
  downloadedAt: string;
  modifications: string;
  /** Mood/semantic tags for scoring (e.g. ominous, warm). */
  tags: string[];
  /** Defaults to `"file"` when omitted (legacy manifests). */
  sourceKind?: AssetSourceKind;
}

export interface ScoringTag {
  id: ScoringTagId;
  label: string;
  /** Added to reach when a retained beat carries this tag (millipoints; may be negative). */
  reachMilli: Milli;
  /**
   * Added to contextLoss when a context-critical beat carrying this tag is omitted.
   * See scoreEdit capped formula in scoring/score.ts.
   */
  contextLossMilli: Milli;
}

export interface AudienceMetadata {
  id: string;
  label: string;
  /** Preferred emotional intensity band (millipoints). */
  preferredIntensityMilli: Milli;
  noveltyWeightMilli: Milli;
  credibilitySensitivityMilli: Milli;
}

export interface TrendMetadata {
  id: TrendId;
  label: string;
  /**
   * Peak reach boost (millipoints) when every matchingTagId appears on retained beats.
   * Partial matches scale linearly: present/total * reachBoostMilli.
   */
  reachBoostMilli: Milli;
  matchingTagIds: ScoringTagId[];
}

export interface ExportSafetyMetadata {
  fictionalSubjectsOnly: boolean;
  allowsIdentifiablePeople: boolean;
  contentWarnings: string[];
  requiredWatermark: string;
  attributionRequired: boolean;
}

/**
 * Authored fictional appearance for a CastSlot.
 * Casting / Reconstruction is not a cosmetic skin menu — variants change provenance.
 */
export interface CastVariant {
  id: CastVariantId;
  label: string;
  /** True only for the source-faithful / default appearance. */
  sourceFaithful: boolean;
  /** Declared visual asset used when this variant is active. */
  visualAssetId: AssetId;
  /**
   * Deterministic Reach contribution when this variant is the active cast (may be negative).
   * Source-faithful variants should use 0.
   */
  presentationReachMilli: Milli;
  /**
   * Base provenance risk when this alternate is chosen (0 for source-faithful).
   * Score-time disclosure can reduce applied risk; it need not erase it.
   * Folded into delayed credDelta via PROVENANCE_CRED_PENALTY_WEIGHT_MILLI.
   */
  provenanceRiskMilli: Milli;
  /** Mood/semantic tags for diagnostics and future systems. */
  tags: string[];
}

/**
 * Authored cast slot on a SourcePack entity.
 * Packs may omit castSlots or supply an empty list.
 */
export interface CastSlot {
  id: CastSlotId;
  label: string;
  entityId: EntityId;
  /** Must reference the single sourceFaithful variant. */
  defaultVariantId: CastVariantId;
  variants: CastVariant[];
}

/**
 * Fixed caption segment — authored token IDs always present in the expansion.
 * UI labels live only in packs; contracts carry IDs only.
 */
export interface HeadlineFixedSegment {
  kind: "fixed";
  id: string;
  tokenIds: CaptionTokenId[];
}

/** One selectable fill for a headline slot (1+ tokens). */
export interface HeadlineSlotOption {
  id: string;
  tokenIds: CaptionTokenId[];
}

/**
 * Editable headline slot — player picks one option; every option must keep
 * the full expansion grammar-valid against the pack caption grammar.
 */
export interface HeadlineSlotSegment {
  kind: "slot";
  id: string;
  defaultOptionId: string;
  options: HeadlineSlotOption[];
}

export type HeadlineTemplateSegment = HeadlineFixedSegment | HeadlineSlotSegment;

/**
 * Authoring/UI metadata for headline remix cards.
 * Applying a card still emits the existing `text` op — templates are not EditGraph state.
 */
export interface HeadlineTemplate {
  id: string;
  /** Pack-local player-facing card title (not duplicated in core UI strings). */
  label: string;
  segments: HeadlineTemplateSegment[];
}

/** Renderer-neutral look / grade treatment with bounded presentation scoring. */
export interface LookTreatment {
  id: string;
  label: string;
  tags: string[];
  /** 1000 = neutral; authored millipoint grade knobs. */
  contrastMilli: Milli;
  saturationMilli: Milli;
  warmthMilli: Milli;
  vignetteMilli: Milli;
  intensitySupported: boolean;
  defaultIntensityMilli: Milli;
  /** Bounded Reach contribution when this look is active (may be negative). */
  presentationReachMilli: Milli;
  productionMilli: Milli;
}

/** Per-beat camera motion preset (deterministic millipoint parameters). */
export interface CameraMotionPreset {
  id: string;
  label: string;
  tags: string[];
  amplitudeMilli: Milli;
  frequencyMilli: Milli;
  zoomDeltaMilli: Milli;
  presentationReachMilli: Milli;
  productionMilli: Milli;
}

/** Beat-boundary transition preset with max duration constraint. */
export interface TransitionPreset {
  id: string;
  label: string;
  tags: string[];
  maxDurationTicks: Tick;
  defaultDurationTicks: Tick;
  presentationReachMilli: Milli;
  productionMilli: Milli;
}

export type StickerRenderDescriptor =
  | {
      kind: "procedural";
      shape: "badge" | "burst" | "arrow" | "underline" | "frame" | "dot" | "star" | "text";
      /** Optional short badge text for procedural text/shapes. */
      text?: string;
      colorTag: string;
    }
  | {
      kind: "asset";
      assetId: AssetId;
    };

/** Authored sticker definition — prefer procedural descriptors over assets. */
export interface StickerOption {
  id: string;
  label: string;
  tags: string[];
  render: StickerRenderDescriptor;
  defaultScaleMilli: Milli;
  defaultRotationMilli: Milli;
  presentationReachMilli: Milli;
  productionMilli: Milli;
  /** When set, sticker may only be placed on these beats. */
  allowedBeatIds?: BeatId[];
}

/**
 * Authored one-shot SFX catalog entry.
 * `assetId` is optional until the audio-assets milestone provides real manifests.
 */
export interface SfxOption {
  id: string;
  label: string;
  tags: string[];
  /** When present, must reference an audio AssetManifestEntry. */
  assetId?: AssetId;
  defaultAtTick: Tick;
  defaultDurationTicks: Tick;
  defaultVolumeMilli: Milli;
  presentationReachMilli: Milli;
  productionMilli: Milli;
}

export interface SourcePack {
  schemaVersion: SourcePackSchemaVersion;
  id: ScenarioId;
  version: string;
  title: string;
  contentRating: ContentRating;
  license: LicenseManifest;
  canonicalRecord: CanonicalEventRecord;
  beats: SemanticBeat[];
  entities: Entity[];
  claims: Claim[];
  ambiguityNotes: AmbiguityNote[];
  captionVocabulary: CaptionVocabulary;
  captionGrammar: CaptionGrammar;
  assets: AssetManifestEntry[];
  /**
   * Renderer-consumable media bindings.
   * At least one variant required; bakeoff samples use assertBakeoffRenderModeCoverage.
   */
  renderVariants: RenderVariant[];
  /**
   * Optional Casting / Reconstruction slots. Absent or empty = no casting for this pack.
   * Existing packs without casting remain valid.
   */
  castSlots?: CastSlot[];
  /**
   * Optional headline remix templates (authoring/UI). Absent/empty remains valid.
   * Emit existing `text` ops — never stored as EditGraph slots.
   */
  headlineTemplates?: HeadlineTemplate[];
  /** Optional look/filter catalog. Absent/empty remains valid. */
  lookTreatments?: LookTreatment[];
  /** Optional per-beat camera motion catalog. Absent/empty remains valid. */
  cameraMotions?: CameraMotionPreset[];
  /** Optional beat-boundary transition catalog. Absent/empty remains valid. */
  transitionPresets?: TransitionPreset[];
  /** Optional sticker catalog. Absent/empty remains valid. */
  stickerOptions?: StickerOption[];
  /**
   * Optional keyed SFX catalog (separate from music bed assets).
   * Entries may omit `assetId` until audio binaries are wired.
   */
  sfxOptions?: SfxOption[];
  scoringTags: ScoringTag[];
  audience: AudienceMetadata;
  trends: TrendMetadata[];
  exportSafety: ExportSafetyMetadata;
  /** Ticks per second for this pack's authored timelines. */
  tickRate: number;
}
